import type { OpeningId } from "../lessons/ids.ts";
import type { OpeningIntro } from "../trainer/openingIntros.ts";
import type { OpeningGuide } from "./openingGuides.ts";

type LegacyOpenings = (typeof import("@data/openings.js"))["OPENINGS"];
export type LegacyOpeningRecord = LegacyOpenings[string];

type CourseCopy = {
  guides: Record<OpeningId, OpeningGuide>;
  intros: Record<OpeningId, OpeningIntro>;
  openings: LegacyOpenings;
};

// Every course's About-page text, first-move intro and legacy opening record
// download together, on demand. The Openings page loads them to draw each
// card's opening moves, and a Theory page reuses the same download. The
// catalog's names and taglines come from the manifest, so this chunk stays
// out of the first page load.
let copy: CourseCopy | null = null;
let pending: Promise<CourseCopy> | null = null;

/** Download the course copy (once). */
export function loadCourseCopy(): Promise<CourseCopy> {
  if (copy) return Promise.resolve(copy);
  if (!pending) {
    const request = Promise.all([
      import("./openingGuides.ts"),
      import("../trainer/openingIntros.ts"),
      import("@data/openings.js"),
    ]).then(([guides, intros, openings]) => {
      copy = {
        guides: guides.OPENING_GUIDES,
        intros: intros.OPENING_INTROS,
        openings: openings.OPENINGS,
      };
      return copy;
    });
    // A failed download (say, offline) may be retried later.
    request.catch(() => {
      if (pending === request) pending = null;
    });
    pending = request;
  }
  return pending;
}

/** The course's About-page guide, once loadCourseCopy has finished. */
export function loadedOpeningGuide(id: OpeningId): OpeningGuide | null {
  return copy?.guides[id] || null;
}

/** The course's first-move intro, once loadCourseCopy has finished. */
export function loadedOpeningIntro(id: OpeningId): OpeningIntro | null {
  return copy?.intros[id] || null;
}

/** The course's legacy opening record, once loadCourseCopy has finished. */
export function loadedOpeningRecord(id: string): LegacyOpeningRecord | null {
  return copy?.openings[id] || null;
}
